import React from 'react';
import ReactDOM from 'react-dom/client';
import ChatWidget from './ChatWidget';

interface Config {
  currency: string;
  user: any;
  language: string;
  sessionId: string | null;
}

interface WWXChatWidgetConfig {
  targetElementId: string;
  currency?: string;
  user?: any;
  language?: string;
  sessionId?: string | null;
  startVisible?: boolean;
}

interface WWXChatWidgetInterface {
  init: (config: WWXChatWidgetConfig) => void;
  show: () => void;
  hide: () => void;
  setCurrency: (currency: string) => void;
  setUser: (user: any) => void;
  setLanguage: (language: string) => void;
  setSessionId: (sessionId: string | null) => void;
  isInitialized: () => boolean;
  isVisible: () => boolean;
}

let targetElement: HTMLElement | null = null;
let currentConfig: Config = {
  currency: 'HKD',
  user: null,
  language: 'EN',
  sessionId: null,
};
let isWidgetVisible: boolean = false;
let reactRoot: ReactDOM.Root | null = null; // For React 18

const renderWidget = (): void => {
  if (targetElement) {
    const widgetProps = {
      initialConfig: { ...currentConfig },
      isVisible: isWidgetVisible,
      sessionId: currentConfig.sessionId,
      onClose: () => { // Add a way to close the widget from within
        if (window.WWXChatWidget) {
          window.WWXChatWidget.hide();
        }
      }
    };

    if (!reactRoot) {
        reactRoot = ReactDOM.createRoot(targetElement);
    }
    reactRoot.render(<ChatWidget {...widgetProps} />);
  }
};

const WWXChatWidget: WWXChatWidgetInterface = {
  init: (config: WWXChatWidgetConfig) => {
    if (!config || !config.targetElementId) {
      console.error("WWX Chat Widget: 'targetElementId' is required in config.");
      return;
    }
    targetElement = document.getElementById(config.targetElementId);
    if (!targetElement) {
      console.error(`WWX Chat Widget: Target element with ID '${config.targetElementId}' not found.`);
      return;
    }

    currentConfig.currency = config.currency || currentConfig.currency;
    currentConfig.user = config.user || currentConfig.user;
    currentConfig.language = config.language || currentConfig.language;
    currentConfig.sessionId = config.sessionId || currentConfig.sessionId;
    isWidgetVisible = config.startVisible || false; // Optionally start visible

    renderWidget();
  },

  show: () => {
    isWidgetVisible = true;
    renderWidget();
  },

  hide: () => {
    isWidgetVisible = false;
    renderWidget();
  },

  setCurrency: (currency: string) => {
    if (typeof currency === 'string') {
      currentConfig.currency = currency;
      renderWidget();
    } else {
      console.warn("WWX Chat Widget: setCurrency expects a string.");
    }
  },

  setUser: (user: string) => {
    // User is JWT string
    currentConfig.user = user;
    renderWidget();
  },

  setLanguage: (language: string) => {
    if (typeof language === 'string') {
      currentConfig.language = language;
      renderWidget();
    } else {
      console.warn("WWX Chat Widget: setLanguage expects a string.");
    }
  },

  setSessionId: (sessionId: string | null) => {
    currentConfig.sessionId = sessionId;
    renderWidget();
  },

  isInitialized: () => !!targetElement,
  isVisible: () => isWidgetVisible,
};

// Extend the Window interface to include WWXChatWidget
declare global {
  interface Window {
    WWXChatWidget: WWXChatWidgetInterface;
  }
}

// Explicitly attach to window object to ensure global availability
window.WWXChatWidget = WWXChatWidget;

// Also make it available as a named export for potential future use
export default WWXChatWidget;
